import {
  ChatToolBase,
  ChatToolDisclosure,
  type ChatToolStatus,
} from "@repo/ui/chat/chatToolCall";
import { TextSkeleton } from "@repo/ui/Skeletons/skeletons";
import type { UIMessage } from "ai";
import type { FC } from "react";
import { useAutomationRunThreadMessagesQuery } from "../../api/entities/automationRun.js";
import { RunTranscript } from "./runTranscript.js";
import { useRunTranscriptContext } from "./runTranscriptContext.js";
import { useAutomationRunMessagesLive } from "./useAutomationRunMessagesLive.js";

export interface SubagentTranscriptProps {
  /** The subagent's Mastra thread; the transcript is streamed or fetched by it. */
  threadId: string;
  /** The tool row's label, e.g. "Detecting score movement for Avery". */
  label: string;
  status: ChatToolStatus;
}

/** A thread with no assistant content yet has nothing to expand into. */
function checkHasContent(messages: readonly UIMessage[]): boolean {
  return messages.some((message) => message.role === "assistant");
}

/** The row plus transcript, once the messages are in hand: expandable when the
 * thread has content, otherwise a plain tool-status line (no chevron over an
 * empty panel). */
const SubagentTranscriptTool: FC<
  Pick<SubagentTranscriptProps, "label" | "status"> & {
    messages: readonly UIMessage[];
    /** True while the subagent's thread is still streaming in. */
    isStreaming?: boolean;
  }
> = ({ label, status, messages, isStreaming }) => {
  if (!checkHasContent(messages)) {
    return <ChatToolBase status={status}>{label}</ChatToolBase>;
  }
  return (
    <ChatToolDisclosure status={status} label={label}>
      <RunTranscript messages={messages} isStreaming={isStreaming} />
    </ChatToolDisclosure>
  );
};

/**
 * An in-progress subagent's thread. It tails the subagent's streamed events
 * through {@link useAutomationRunMessagesLive} — always, since this component
 * stays mounted whether or not the row is expanded.
 */
const SubagentTranscriptLive: FC<SubagentTranscriptProps> = (props) => {
  const { threadId, label, status } = props;
  const { messages } = useAutomationRunMessagesLive({ threadId });
  return (
    <SubagentTranscriptTool
      label={label}
      status={status}
      messages={messages}
      isStreaming
    />
  );
};

/** A finished subagent's persisted transcript, fetched when the panel mounts. */
const SubagentTranscriptFetched: FC<{ threadId: string }> = ({ threadId }) => {
  const { automationId, runId } = useRunTranscriptContext();
  const { data: messages, isLoading } = useAutomationRunThreadMessagesQuery({
    automationId,
    automationRunId: runId,
    threadId,
  });

  if (isLoading) {
    return <TextSkeleton />;
  }

  return <RunTranscript messages={messages ?? []} />;
};

/**
 * One subagent's thread, nested inside its delegation tool call — the tool call
 * renders as an ordinary chat tool row that happens to expand into the
 * subagent's own transcript. While the run is going and this subagent is
 * working, the transcript streams live; otherwise the row expands into the
 * persisted transcript, fetched lazily. The run's identity (and the
 * styleguide's static message source, when present) comes from the ambient
 * `RunTranscriptContext`.
 */
export const SubagentTranscript: FC<SubagentTranscriptProps> = (props) => {
  const { threadId, label, status } = props;
  const { isRunTerminal, resolveThreadMessages } = useRunTranscriptContext();

  if (resolveThreadMessages) {
    return (
      <SubagentTranscriptTool
        label={label}
        status={status}
        messages={resolveThreadMessages(threadId)}
        isStreaming={status.type === "running"}
      />
    );
  }

  if (!isRunTerminal && status.type === "running") {
    return <SubagentTranscriptLive {...props} />;
  }

  // A finished delegation always has its persisted transcript to expand into.
  return (
    <ChatToolDisclosure status={status} label={label}>
      <SubagentTranscriptFetched threadId={threadId} />
    </ChatToolDisclosure>
  );
};
